import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ADMIN_MENU_ITEMS, defaultMenuPermissions } from "@/constants/adminMenu";
import SignatureUpload from "@/components/SignatureUpload";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  UserPlus, Users, ShieldCheck, User, Wrench, ClipboardCheck,
  Eye, EyeOff, Copy, KeyRound, Pencil, Trash2,
} from "lucide-react";

const ROLE_CONFIG = {
  admin: { label: "Administrador", color: "bg-red-100 text-red-800 border-red-200", icon: ShieldCheck },
  operator: { label: "Operador", color: "bg-blue-100 text-blue-800 border-blue-200", icon: User },
  auxiliar: { label: "Auxiliar", color: "bg-purple-100 text-purple-800 border-purple-200", icon: Wrench },
  auditor: { label: "Auditor", color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: ClipboardCheck },
};
const STAFF_ROLES = ["admin", "operator", "auxiliar", "auditor"];

const emptyForm = { full_name: "", role: "auditor", email: "", username: "", password: "", menu_permissions: defaultMenuPermissions("auditor") };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [newPassword, setNewPassword] = useState("");
  const [generatedPassword, setGeneratedPassword] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/auth/users");
      setUsers(res.data || []);
    } catch {
      toast.error("Error al cargar usuarios");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const copyText = (t) => { navigator.clipboard?.writeText(t); toast.success("Copiado"); };

  const handleCreate = async () => {
    if (!form.full_name.trim()) return toast.error("Ingrese el nombre completo");
    setSubmitting(true);
    try {
      const res = await api.post("/auth/users", {
        full_name: form.full_name.trim(),
        role: form.role,
        email: form.email.trim() || null,
        username: form.username.trim() || null,
        password: form.password.trim() || null,
        menu_permissions: form.menu_permissions,
      });
      toast.success(`Usuario creado. Contraseña: ${res.data.initial_password}`);
      setShowCreate(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear usuario");
    } finally { setSubmitting(false); }
  };

  const handleEdit = async () => {
    if (!editUser) return;
    setSubmitting(true);
    try {
      await api.patch(`/auth/users/${editUser.id}`, {
        full_name: form.full_name.trim(),
        role: form.role,
        email: form.email.trim() || null,
        username: form.username.trim() || null,
        menu_permissions: form.menu_permissions,
      });
      toast.success("Usuario actualizado");
      setEditUser(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar");
    } finally { setSubmitting(false); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/auth/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Usuario desactivado" : "Usuario activado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cambiar estado");
    }
  };

  const handleReset = async () => {
    if (!resetUser) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/auth/users/${resetUser.id}/password`, { password: newPassword.trim() || null });
      setGeneratedPassword(res.data.initial_password);
      toast.success("Contraseña actualizada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al restablecer");
    } finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    setSubmitting(true);
    try {
      await api.delete(`/auth/users/${deleteUser.id}`);
      toast.success("Usuario eliminado");
      setDeleteUser(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    } finally { setSubmitting(false); }
  };

  const openEdit = (u) => {
    setForm({
      full_name: u.full_name || "", role: u.role, email: u.email || "", username: u.username || "", password: "",
      menu_permissions: Array.isArray(u.menu_permissions) ? u.menu_permissions : defaultMenuPermissions(u.role),
    });
    setEditUser(u);
  };
  const openReset = (u) => { setNewPassword(""); setGeneratedPassword(""); setResetUser(u); };

  const uploadUserSignature = async (dataUri) => {
    const res = await api.post(`/auth/users/${editUser.id}/signature`, { data_uri: dataUri });
    setEditUser((u) => ({ ...u, signature_url: res.data.signature_url }));
    load();
  };
  const removeUserSignature = async () => {
    await api.post(`/auth/users/${editUser.id}/signature`, { data_uri: null });
    setEditUser((u) => ({ ...u, signature_url: null }));
    load();
  };

  const toggleMenu = (key) => setForm(f => {
    const has = f.menu_permissions.includes(key);
    return { ...f, menu_permissions: has ? f.menu_permissions.filter(k => k !== key) : [...f.menu_permissions, key] };
  });

  const RoleSelector = () => (
    <div className="grid grid-cols-2 gap-2">
      {STAFF_ROLES.map(r => (
        <button
          key={r}
          type="button"
          onClick={() => setForm(f => ({ ...f, role: r, menu_permissions: defaultMenuPermissions(r) }))}
          className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center gap-1.5 justify-center ${
            form.role === r ? `${ROLE_CONFIG[r].color} border-current` : "border-border text-muted-foreground"
          }`}
          data-testid={`user-role-${r}`}
        >
          {React.createElement(ROLE_CONFIG[r].icon, { className: "w-3.5 h-3.5" })}
          {ROLE_CONFIG[r].label}
        </button>
      ))}
    </div>
  );

  const MenuPermissions = () => (
    <div data-testid="menu-permissions">
      <Label className="text-xs font-semibold mb-1 block">Acceso a pestañas del menú</Label>
      {form.role === "admin" ? (
        <p className="text-xs text-muted-foreground bg-secondary rounded-lg px-3 py-2" data-testid="menu-permissions-admin-note">
          El administrador siempre tiene acceso total a todas las pestañas.
        </p>
      ) : (
        <>
          <p className="text-[11px] text-muted-foreground mb-2">
            Se preseleccionan las pestañas del rol. Añada o quite acceso según necesite.
          </p>
          <div className="grid grid-cols-2 gap-1.5 max-h-52 overflow-y-auto pr-1">
            {ADMIN_MENU_ITEMS.map(item => {
              const checked = form.menu_permissions.includes(item.key);
              return (
                <label
                  key={item.key}
                  className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs cursor-pointer transition-colors ${
                    checked ? "border-[#FFD62B] bg-[#FFD62B]/10" : "border-border"
                  }`}
                  data-testid={`menu-perm-${item.key}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleMenu(item.key)}
                    className="accent-[#FFD62B] w-3.5 h-3.5"
                    data-testid={`menu-perm-checkbox-${item.key}`}
                  />
                  <span className="flex-1">{item.label}</span>
                </label>
              );
            })}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#FFD62B]/20 flex items-center justify-center">
            <Users className="w-5 h-5 text-[#FFD62B]" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">Gestión de Usuarios</h1>
            <p className="text-sm text-muted-foreground">Crear y administrar usuarios del sistema y sus roles (técnicos y clientes se gestionan en sus propias secciones)</p>
          </div>
        </div>
        <Button
          onClick={() => { setForm(emptyForm); setShowCreate(true); }}
          className="bg-[#FFD62B] hover:bg-[#FFD62B]/90 text-black font-semibold"
          data-testid="create-user-btn"
        >
          <UserPlus className="w-4 h-4 mr-1.5" /> Nuevo usuario
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-16 rounded-xl bg-secondary animate-pulse" />)}
        </div>
      ) : users.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground text-sm">Aún no hay usuarios del sistema. Cree el primero.</Card>
      ) : (
        <div className="space-y-2">
          {users.map(u => {
            const rc = ROLE_CONFIG[u.role] || ROLE_CONFIG.operator;
            const RIcon = rc.icon;
            return (
              <Card key={u.id} className={`p-3 flex items-center gap-3 flex-wrap ${!u.is_active ? "opacity-60" : ""}`} data-testid={`user-card-${u.id}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${rc.color.split(" ")[0]}`}>
                  <RIcon className={`w-4 h-4 ${rc.color.split(" ")[1]}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2">
                    {u.full_name}
                    {!u.is_active && <span className="text-[10px] text-red-600 font-semibold">(Inactivo)</span>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {u.username && <span>👤 {u.username}</span>}
                    {u.username && u.email && <span> · </span>}
                    {u.email && <span>{u.email}</span>}
                  </div>
                  {u.initial_password && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <span className="font-mono">
                        {revealed[u.id] ? u.initial_password : "••••••••"}
                      </span>
                      <button onClick={() => setRevealed(r => ({ ...r, [u.id]: !r[u.id] }))} data-testid={`reveal-pass-${u.id}`} title="Ver contraseña">
                        {revealed[u.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                      {revealed[u.id] && (
                        <button onClick={() => copyText(u.initial_password)} title="Copiar"><Copy className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  )}
                </div>
                <Badge variant="outline" className={`text-[10px] ${rc.color}`}>{rc.label}</Badge>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} title="Editar">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openReset(u)} data-testid={`reset-user-${u.id}`} title="Restablecer contraseña">
                    <KeyRound className="w-4 h-4" />
                  </Button>
                  <div className="flex items-center gap-1" title={u.is_active ? "Desactivar" : "Activar"}>
                    <Switch checked={!!u.is_active} onCheckedChange={() => toggleActive(u)} data-testid={`toggle-user-${u.id}`} />
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => setDeleteUser(u)} data-testid={`delete-user-${u.id}`} title="Eliminar">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={(o) => { if (!o) setShowCreate(false); }}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Crear usuario</DialogTitle>
            <DialogDescription>Ingrese los datos del nuevo usuario del sistema</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold mb-1 block">Nombre completo</Label>
              <Input value={form.full_name} onChange={e => setForm(f => ({...f, full_name: e.target.value}))} placeholder="Nombre del usuario" data-testid="new-user-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Rol</Label>
              <RoleSelector />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Usuario (opcional)</Label>
              <Input value={form.username} onChange={e => setForm(f => ({...f, username: e.target.value}))} placeholder="Se genera automáticamente si se deja vacío" data-testid="new-user-username" />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Email (opcional)</Label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="correo@empresa.sv" data-testid="new-user-email" />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Contraseña (opcional)</Label>
              <Input value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} placeholder="Se genera automáticamente si se deja vacía" data-testid="new-user-password" />
            </div>
            <MenuPermissions />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={submitting} className="bg-[#FFD62B] hover:bg-[#FFD62B]/90 text-black font-semibold" data-testid="create-user-submit">
              {submitting ? "Creando..." : "Crear usuario"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editUser} onOpenChange={(o) => { if (!o) setEditUser(null); }}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar usuario</DialogTitle>
            <DialogDescription>Modifique los datos del usuario</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold mb-1 block">Nombre completo</Label>
              <Input value={form.full_name} onChange={e => setForm(f => ({...f, full_name: e.target.value}))} data-testid="edit-user-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Rol</Label>
              <RoleSelector />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Usuario</Label>
              <Input value={form.username} onChange={e => setForm(f => ({...f, username: e.target.value}))} data-testid="edit-user-username" />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} data-testid="edit-user-email" />
            </div>
            <MenuPermissions />
            <div className="pt-1 border-t">
              <SignatureUpload
                signatureUrl={editUser?.signature_url}
                onUpload={uploadUserSignature}
                onRemove={removeUserSignature}
                testid="user-signature"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>Cancelar</Button>
            <Button onClick={handleEdit} disabled={submitting} className="bg-[#FFD62B] hover:bg-[#FFD62B]/90 text-black font-semibold" data-testid="edit-user-submit">
              {submitting ? "Guardando..." : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetUser} onOpenChange={(o) => { if (!o) { setResetUser(null); setGeneratedPassword(""); } }}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Restablecer contraseña</DialogTitle>
            <DialogDescription>{resetUser?.full_name}</DialogDescription>
          </DialogHeader>
          {generatedPassword ? (
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Nueva contraseña</Label>
              <div className="flex items-center gap-2 bg-secondary rounded-lg px-3 py-2">
                <span className="font-mono text-sm flex-1">{generatedPassword}</span>
                <button onClick={() => copyText(generatedPassword)} title="Copiar"><Copy className="w-4 h-4" /></button>
              </div>
              <p className="text-xs text-muted-foreground">Comparta esta contraseña con el usuario.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold mb-1 block">Nueva contraseña (opcional)</Label>
                <Input value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Vacío = generar automáticamente" data-testid="reset-user-password" />
              </div>
            </div>
          )}
          <DialogFooter>
            {generatedPassword ? (
              <Button onClick={() => { setResetUser(null); setGeneratedPassword(""); }} className="bg-[#FFD62B] hover:bg-[#FFD62B]/90 text-black font-semibold">Listo</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setResetUser(null)}>Cancelar</Button>
                <Button onClick={handleReset} disabled={submitting} className="bg-[#FFD62B] hover:bg-[#FFD62B]/90 text-black font-semibold" data-testid="reset-user-submit">
                  {submitting ? "Aplicando..." : "Restablecer"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteUser} onOpenChange={(o) => { if (!o) setDeleteUser(null); }}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Eliminar usuario</DialogTitle>
            <DialogDescription>¿Seguro que desea eliminar a <b>{deleteUser?.full_name}</b>? Esta acción no se puede deshacer.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUser(null)}>Cancelar</Button>
            <Button onClick={handleDelete} disabled={submitting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-user">
              {submitting ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
